import React, { useMemo } from 'react'
import { IClinicNumber, IPortDetailsStep2 } from '@dataTypes/clinic.type'
import {
  formatPhoneNumber,
  normalizePhoneNumber,
} from '@utils/numberFormatter.util'
import { useAppSelector } from '@store/hooks'
import { LinkButton } from '@enamelai/design-system'
import ReviewSections from '../../../shared/ReviewSections'

interface ReviewInfoProps {
  numberData: IClinicNumber
  detailsStep2Data: IPortDetailsStep2
  existingDocumentLabel?: string | null
}

const ReviewInfo: React.FC<ReviewInfoProps> = ({
  numberData,
  detailsStep2Data,
  existingDocumentLabel = null,
}) => {
  const clinicData = useAppSelector((state) => state.clinic.selectedClinic)
  const selectedNumbers = useMemo(
    () =>
      numberData.phone_number
        .split('\n')
        .map((item) => item.trim())
        .filter((item) => item !== '')
        .map((item) => formatPhoneNumber(normalizePhoneNumber(item), false)),
    [numberData.phone_number],
  )
  const documentName = detailsStep2Data.billing_statement_file?.name?.trim()
    ? detailsStep2Data.billing_statement_file.name
    : existingDocumentLabel || 'Billing statement.pdf'
  const canViewDocument = !!detailsStep2Data.billing_statement_file

  const handleViewDocument = (): void => {
    if (!detailsStep2Data.billing_statement_file) return
    const fileUrl = URL.createObjectURL(detailsStep2Data.billing_statement_file)
    window.open(fileUrl, '_blank', 'noopener,noreferrer')
    setTimeout(() => URL.revokeObjectURL(fileUrl), 10000)
  }

  return (
    <ReviewSections
      sections={[
        {
          id: 'number',
          title: 'Number',
          showInfo: true,
          fields: [
            {
              label: 'Selected number',
              value: selectedNumbers.join(', ') || '-',
            },
            { label: 'Carrier', value: numberData.phone_carrier || '-' },
          ],
        },
        {
          id: 'contact',
          title: 'Contact',
          showInfo: true,
          fields: [
            { label: 'Full name', value: detailsStep2Data.full_name || '-' },
            {
              label: 'Email address',
              value: detailsStep2Data.email_address || '-',
            },
          ],
        },
        {
          id: 'location',
          title: 'Location',
          showInfo: true,
          fields: [
            { label: 'Address', value: clinicData?.address || '-' },
            { label: 'City', value: clinicData?.city || '-' },
            { label: 'State', value: clinicData?.state || '-' },
            { label: 'ZIP', value: clinicData?.zip || '-' },
          ],
        },
        {
          id: 'documents',
          title: 'Documents',
          content: (
            <span>
              {documentName}{' '}
              <LinkButton
                type="button"
                size="sm"
                disabled={!canViewDocument}
                onClick={handleViewDocument}
              >
                View
              </LinkButton>
            </span>
          ),
        },
      ]}
    />
  )
}

export default ReviewInfo
